import { useEffect, useState } from "react";
import { matchPath, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ApiError, me } from "./api/client";
import type { Me } from "./api/models";
import { Shell } from "./components/Shell";
import { errorMessage } from "./lib/format";
import { CardsPage } from "./pages/cards/CardsPage";
import { FetchesPage } from "./pages/fetches/FetchesPage";
import { InvitePage } from "./pages/invite/InvitePage";
import { LoginPage } from "./pages/login/LoginPage";
import { ReviewPage } from "./pages/review/ReviewPage";
import { RunPage } from "./pages/runs/RunPage";
import { RunsPage } from "./pages/runs/RunsPage";
import { TokensPage } from "./pages/tokens/TokensPage";
import type { AuthStage, Session } from "./platform/firebase";

export function App({ session }: { session: Session }) {
  const [stage, setStage] = useState<AuthStage>("restoring");
  const [firebaseEmail, setFirebaseEmail] = useState<string | null>(null);
  const [resolved, setResolved] = useState<Me | null>(null);
  const [denied, setDenied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();

  useEffect(
    () =>
      session.watch((next, user) => {
        setStage(next);
        setFirebaseEmail(user?.email ?? null);
        setResolved(null);
        setDenied(null);
      }),
    [session],
  );

  // Once Firebase knows whether a user is restored, ask the backend who that is:
  // the allowlist lives there, and so does the local dev bypass.
  useEffect(() => {
    if (stage === "restoring") return;
    me()
      .then(setResolved)
      .catch((e) => {
        if (e instanceof ApiError && (e.status === 403 || e.status === 401)) setDenied(e.message);
        else setError(errorMessage(e));
      });
  }, [stage]);

  const signOut = async () => {
    await session.signOut();
    window.location.assign("/login");
  };

  if (matchPath("/invite/:token", location.pathname))
    return <InvitePage session={session} stage={stage} firebaseEmail={firebaseEmail} />;
  if (error) return <main className="error">Cannot reach the API: {error}</main>;
  if (stage === "restoring" || (!resolved && !denied)) return <main className="muted">Loading…</main>;
  if (!resolved?.user)
    return <LoginPage session={session} denied={denied} firebaseEmail={firebaseEmail} onSignOut={signOut} />;

  return (
    <Routes>
      <Route element={<Shell email={resolved.user.email} onLogout={signOut} />}>
        <Route path="/" element={<CardsPage />} />
        <Route path="/cards/:id" element={<ReviewPage />} />
        <Route path="/runs" element={<RunsPage />} />
        <Route path="/runs/:id" element={<RunPage />} />
        <Route path="/fetches" element={<FetchesPage />} />
        <Route path="/tokens" element={<TokensPage />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
